import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Bot, MessageSquare, Inbox, FileSearch,
  BookOpen, Shield, LogOut, Zap, Users2, Building2, TrendingUp,
  Wrench, Activity, ShieldCheck, ScrollText, GitBranch,
  AlertOctagon, Radio, Coins, Workflow, Star, Key,
  Plug, FileOutput, CreditCard, BarChart3,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import CommandPalette from "@/components/CommandPalette";
import NotificationsBell from "@/components/NotificationsBell";
import HelpChat from "@/components/HelpChat";
import BrandMark from "@/components/BrandMark";
import TrialBanner from "@/components/TrialBanner";
import { trackPurchase } from "@/lib/analytics";

const SECTIONS = [
  {
    label: "PRINCIPAL",
    items: [
      { to: "/dashboard", label: "Panel", icon: LayoutDashboard, testId: "nav-dashboard" },
      { to: "/resultados", label: "Resultados", icon: BarChart3, testId: "nav-resultados" },
      { to: "/agents", label: "Agentes", icon: Bot, testId: "nav-agents" },
      { to: "/asistente", label: "Atención al cliente", icon: MessageSquare, testId: "nav-asistente" },
      { to: "/council", label: "Consejo", icon: Users2, testId: "nav-council" },
      { to: "/templates", label: "Tu negocio", icon: Building2, testId: "nav-templates" },
    ],
  },
  {
    label: "TU NEGOCIO",
    items: [
      { to: "/crm", label: "Clientes (CRM)", icon: TrendingUp, testId: "nav-crm" },
      { to: "/captacion", label: "Captación", icon: TrendingUp, testId: "nav-captacion" },
      { to: "/tasks", label: "Tareas", icon: ScrollText, testId: "nav-tasks" },
      { to: "/knowledge", label: "Documentos", icon: BookOpen, testId: "nav-knowledge" },
      { to: "/workflows", label: "Flujos de trabajo", icon: Workflow, testId: "nav-workflows" },
      { to: "/acciones", label: "Acciones (n8n)", icon: Plug, testId: "nav-acciones" },
      { to: "/automations", label: "Automatizaciones", icon: Zap, testId: "nav-automations" },
    ],
  },
  {
    label: "EQUIPO Y AJUSTES",
    items: [
      { to: "/members", label: "Miembros", icon: Users2, testId: "nav-members" },
      { to: "/settings/llm", label: "Claves LLM", icon: Key, testId: "nav-llm-keys" },
      { to: "/precios", label: "Planes y precios", icon: Coins, testId: "nav-precios" },
      { to: "/budgets", label: "Presupuestos", icon: Coins, testId: "nav-budgets" },
      { to: "/integrations", label: "Integraciones", icon: Plug, testId: "nav-integrations" },
      { to: "/cost", label: "Coste de IA", icon: Coins, testId: "nav-cost" },
      { to: "/precios", label: "Planes y precios", icon: CreditCard, testId: "nav-pricing" },
    ],
  },
  {
    label: "CONTROL",
    items: [
      { to: "/approvals", label: "Aprobaciones", icon: Inbox, testId: "nav-approvals" },
      { to: "/audit", label: "Auditoría", icon: FileSearch, testId: "nav-audit" },
      { to: "/security", label: "Seguridad", icon: Shield, testId: "nav-security" },
      { to: "/reports", label: "Informes", icon: FileOutput, testId: "nav-reports" },
    ],
  },
  {
    label: "AVANZADO",
    items: [
      { to: "/departments", label: "Departamentos", icon: Building2, testId: "nav-departments" },
      { to: "/system", label: "Modo Sistema", icon: Radio, testId: "nav-system" },
      { to: "/policies", label: "Políticas", icon: FileSearch, testId: "nav-policies" },
      { to: "/playbooks", label: "Playbooks", icon: MessageSquare, testId: "nav-playbooks" },
      { to: "/decisions", label: "Decisiones", icon: GitBranch, testId: "nav-decisions" },
      { to: "/incidents", label: "Incidentes", icon: AlertOctagon, testId: "nav-incidents" },
      { to: "/frameworks", label: "Cumplimiento", icon: ShieldCheck, testId: "nav-frameworks" },
      { to: "/events", label: "Eventos", icon: Activity, testId: "nav-events" },
      { to: "/evaluations", label: "Evaluaciones", icon: Star, testId: "nav-evals" },
      { to: "/api-keys", label: "API y Webhooks", icon: Key, testId: "nav-apikeys" },
      { to: "/tools", label: "Herramientas", icon: Wrench, testId: "nav-tools" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  // Onboarding: si aún no has elegido negocio, te llevamos a la bienvenida.
  React.useEffect(() => {
    if (localStorage.getItem("aios_onboarded")) return;
    api.get("/agents")
      .then((r) => {
        if (!r.data.template) nav("/welcome");
        else localStorage.setItem("aios_onboarded", "1");
      })
      .catch(() => {});
  }, [nav]);

  // Conversión de pago: al volver de Stripe (/dashboard?pago=ok)
  React.useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("pago") === "ok") trackPurchase();
  }, []);

  return (
    <div className="min-h-screen grid grid-cols-[220px_1fr] bg-[var(--bg)]" data-testid="app-layout">
      <aside className="border-r border-[var(--border)] bg-[var(--surface)] flex flex-col relative overflow-hidden">
        <div className="p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <BrandMark className="w-8 h-8" />
            <div className="min-w-0">
              <div className="font-display font-black text-[14px] tracking-tight leading-none">AGENT COMPANY OS</div>
              <div className="label-mono mt-1 truncate">por BeautyOS</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {SECTIONS.map((sec) => (
            <div key={sec.label} className="mb-2">
              <div className="label-mono px-4 pt-3 pb-1">{sec.label}</div>
              {sec.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-4 py-2 text-xs font-medium border-l-2 transition-colors ${
                      isActive
                        ? "border-[var(--accent)] bg-[var(--surface-2)] text-white"
                        : "border-transparent text-[var(--text-dim)] hover:text-white hover:bg-[var(--surface-2)]/60"
                    }`
                  }
                >
                  <n.icon className="w-3.5 h-3.5" strokeWidth={2} />
                  {n.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-[var(--border)]">
          <div className="mb-2">
            <div className="text-[12px] font-semibold text-white truncate">{user?.full_name}</div>
            <div className="label-mono truncate">{user?.company_name}</div>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            size="sm"
            className="w-full rounded-none border-[var(--border)] hover:border-[var(--danger)] hover:text-[var(--danger)] h-8 text-xs"
            data-testid="btn-logout"
          >
            <LogOut className="w-3 h-3 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      <main className="overflow-auto relative">
        <TrialBanner />
        <div className="sticky top-0 z-30 flex items-center justify-end gap-2 px-6 py-3 bg-[var(--bg)]/95 backdrop-blur border-b border-[var(--border)]">
          <button
            onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-[var(--border)] text-xs font-mono text-[var(--text-dim)] hover:border-[var(--accent)] hover:text-white"
            data-testid="cmdk-trigger"
          >
            Buscar…
            <span className="border border-[var(--border)] px-1.5 py-0.5 text-[10px]">⌘K</span>
          </button>
          <NotificationsBell />
        </div>
        <Outlet />
        <CommandPalette />
      </main>
      <HelpChat />
    </div>
  );
}
